import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { CircleAlert, CircleCheck, Info, X } from 'lucide-react';
import { cx } from '@/lib/cx';

export interface ToastInput {
  tone?: 'success' | 'danger' | 'info';
  title: string;
  description?: string;
  /** ms; errors stay until dismissed */
  duration?: number;
}
interface ToastItem extends ToastInput {
  id: number;
}

const ToastCtx = createContext<{ show: (t: ToastInput) => void } | null>(null);

/**
 * Minimal toasts for confirmations of actions whose result is not otherwise visible
 * (e.g. "Invitation sent"). Prefer inline feedback next to the thing that changed.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const dismiss = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback(
    (t: ToastInput) => {
      const id = ++seq.current;
      setItems((xs) => [...xs.slice(-2), { ...t, id }]);
      const duration = t.duration ?? (t.tone === 'danger' ? 0 : 4200);
      if (duration) setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );
  const value = useMemo(() => ({ show }), [show]);
  return (
    <ToastCtx.Provider value={value}>
      {children}
      <div className="toast-region" role="region" aria-label="Notifications" aria-live="polite">
        <AnimatePresence initial={false}>
          {items.map((t) => {
            const Icon = t.tone === 'danger' ? CircleAlert : t.tone === 'success' ? CircleCheck : Info;
            return (
              <motion.div
                key={t.id}
                layout
                initial={{ opacity: 0, y: 12, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 8, transition: { duration: 0.14 } }}
                transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
                className={cx('toast', `tone-${t.tone ?? 'info'}`)}
                role={t.tone === 'danger' ? 'alert' : 'status'}
              >
                <Icon size={16} aria-hidden className="toast-icon" />
                <div className="toast-body">
                  <p className="toast-title">{t.title}</p>
                  {t.description && <p className="toast-desc">{t.description}</p>}
                </div>
                <button type="button" className="toast-close" aria-label="Dismiss notification" onClick={() => dismiss(t.id)}>
                  <X size={14} />
                </button>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast() {
  const v = useContext(ToastCtx);
  if (!v) throw new Error('useToast must be used inside <ToastProvider>');
  return v;
}
